import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { fetchAgendaOccurrences, fetchMyDirectMinistryIds } from '../api/calendar.api';
import { buildCalendarItems } from '../lib/items';
import { fetchAgendaProjects, fetchReadableCalendarProjects } from '@/features/projects';
import { queryKeys, type DateRange } from '@/shared/lib/queryKeys';
import type { CalendarItem, Id } from '@/shared/types/domain';

/**
 * As consultas da grade — **três fontes de dado, uma entrada de cache**
 * (spec-calendar.md §3.5, decisão 54).
 *
 * Uma `useQuery` só, chaveada pelo INTERVALO visível e por nada mais. Filtro não
 * entra na chave porque não vai ao servidor: é recorte de exibição sobre dado já
 * em mãos, e se entrasse cada toque no seletor disparia uma consulta e a tela
 * piscaria a cada clique.
 *
 * As três correm em paralelo porque nenhuma depende da outra, e o merge é função
 * pura (`buildCalendarItems`) — é ela que resolve a precedência entre as
 * origens, e é ela que tem teste.
 *
 * ⚠️ **Esta consulta é do MÊS VISÍVEL. A caixa de entrada tem a própria**
 * (`useCalendarInbox`), e a razão é permanente: o projeto que espera você quase
 * nunca está no mês que você está olhando.
 */
export function useCalendarItems(
  range: DateRange,
  profileId: Id | null,
): UseQueryResult<readonly CalendarItem[]> {
  return useQuery({
    queryKey: queryKeys.calendar(range),
    queryFn: async () => {
      const [agenda, readable, occurrences, ministryIds] = await Promise.all([
        fetchAgendaProjects({ from: range.from, to: range.to }),
        fetchReadableCalendarProjects(range.from, range.to),
        fetchAgendaOccurrences(range.from, range.to),
        // Sem pessoa logada não há "meu" — e não há tela, porque `/calendario`
        // vive sob `RequireAuth`. A guarda existe para o instante entre o
        // primeiro render e a chegada de `useCurrentUser`.
        profileId ? fetchMyDirectMinistryIds(profileId) : Promise.resolve<Id[]>([]),
      ]);

      return buildCalendarItems({
        agenda,
        readable,
        occurrences,
        scope: { profileId, ministryIds },
      });
    },
    staleTime: 30_000,
  });
}
